import { ChangeEvent, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import { Theme, useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  getClientAllUsersRequest,
  getClientSubDivisionListRequest,
  getProcessesProcedureRequest,
  getTenantAllUsersRequest,
} from "../../../../../../redux/clients/slice";
import {
  deliveryMethodOptions,
  invoiceFormatOptions,
  invoiceFrequencyOptions,
} from "../../../../../../utils/Constant";
import Loading from "../../../../../../components/loading/loading";
import { updateOrderHandlingRequest } from "../../../../../../utils/APICalls";
import CancelIcon from "@mui/icons-material/Cancel";
import { useFormik } from "formik";
import * as yup from "yup";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
  clientId: string;
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(id: string, personName: readonly any[], theme: Theme) {
  return {
    fontWeight: personName?.includes(id)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

const renderDeliveryMethodLabel = (
  option: { value?: string; label: string; hint?: string },
  labelOverride?: string
) => (
  <>
    {labelOverride ?? option.label}
    {option.hint && (
      <Typography
        component="span"
        sx={{
          ml: 0.5,
          fontWeight: 400,
          fontSize: "12px",
          color: "var(--text-secondary, #00000099)",
        }}
      >
        {option.hint}
      </Typography>
    )}
  </>
);

type FormValues = {
  orderReceivedDeliveryMethod: string;
  orderReceivedDeliveredTo: string[];
  resultClientDeliveryMethod: string;
  resultClientDeliveredTo: string[];
  invoiceClientFrequency: string;
  invoiceClientDeliveryMethod: string;
  invoiceClientFormat: string;
  invoiceClientDeliveredTo: string[];
  invoiceClientNote: string;
  projectInvoiceDeliveryMethod: string;
  projectInvoiceFormat: string;
  projectInvoiceDeliveredTo: string[];
  projectInvoiceNote: string;
  resultClientNote: string;
};

export default function EditOrderHandling({
  showDialog,
  closeDialog,
  data,
  clientId,
}: props) {
  const theme = useTheme();
  const [resultDivisionCheckbox, setResultDivisionCheckbox] = useState(false);
  const [resultDivisions, setResultDivisions] = useState<any[]>([]);
  const [invoiceDivisionCheckbox, setInvoiceDivisionCheckbox] = useState(false);
  const [invoiceDivisions, setInvoiceDivisions] = useState<any[]>([]);
  const [showLoading, setShowLoading] = useState(false);

  const dispatch = useDispatch();

  const { subDivisionList, tenantAllUsers, clientAllUsers } = useSelector(
    (state: any) => state?.clients
  );

  const ValiditionSchema = yup.object({
    orderReceivedDeliveryMethod: yup
      .string()
      .required("This field can't be empty"),
    orderReceivedDeliveredTo: yup
      .array()
      .min(1, "This field can't be empty")
      .required("This field can't be empty"),
    resultClientDeliveryMethod: resultDivisionCheckbox
      ? yup.string().notRequired()
      : yup.string().required("This field can't be empty"),
    resultClientDeliveredTo: resultDivisionCheckbox
      ? yup.array().notRequired()
      : yup
          .array()
          .min(1, "This field can't be empty")
          .required("This field can't be empty"),
    resultClientNote: yup.string().notRequired(),
    invoiceClientFrequency: invoiceDivisionCheckbox
      ? yup.string().notRequired()
      : yup.string().required("This field can't be empty"),
    invoiceClientDeliveryMethod: invoiceDivisionCheckbox
      ? yup.string().notRequired()
      : yup.string().required("This field can't be empty"),
    invoiceClientFormat: invoiceDivisionCheckbox
      ? yup.string().notRequired()
      : yup.string().required("This field can't be empty"),
    invoiceClientDeliveredTo: invoiceDivisionCheckbox
      ? yup.array().notRequired()
      : yup
          .array()
          .min(1, "This field can't be empty")
          .required("This field can't be empty"),
    invoiceClientNote: yup.string().notRequired(),
    projectInvoiceDeliveryMethod: yup
      .string()
      .required("This field can't be empty"),
    projectInvoiceFormat: yup.string().required("This field can't be empty"),
    projectInvoiceDeliveredTo: yup
      .array()
      .min(1, "This field can't be empty")
      .required("This field can't be empty"),
    projectInvoiceNote: yup.string().notRequired(),
  });

  useEffect(() => {
    dispatch(getTenantAllUsersRequest());
    dispatch(getClientAllUsersRequest({ id: clientId }));
    dispatch(getClientSubDivisionListRequest({ id: clientId }));
  }, [dispatch]); // eslint-disable-line

  useEffect(() => {
    if (subDivisionList?.length > 0) {
      const resultsDivisions = JSON.parse(JSON.stringify(subDivisionList));
      const invoiceDivisions = JSON.parse(JSON.stringify(subDivisionList));
      resultsDivisions.forEach((division: any) => {
        division.deliveryMethod = "";
        division.deliveredTo = [];
        division.note = "";
      });
      setResultDivisions(resultsDivisions);
      invoiceDivisions.forEach((division: any) => {
        division.deliveryMethod = "";
        division.deliveredTo = [];
        division.frequency = "";
        division.format = "";
        division.note = "";
      });
      setInvoiceDivisions(invoiceDivisions);
    }
  }, [subDivisionList]);

  useEffect(() => {
    if (data && tenantAllUsers?.length > 0 && clientAllUsers?.length > 0) {
      if (data.submitResults?.client) {
        setResultDivisionCheckbox(false);
      }

      if (
        data.submitResults?.subDivisions &&
        data.submitResults?.subDivisions.length > 0
      ) {
        setResultDivisionCheckbox(true);
        if (resultDivisions?.length > 0) {
          const divisions = [...resultDivisions];
          const selectedDivisions = data.submitResults.subDivisions;
          divisions.forEach((division: any) => {
            selectedDivisions.forEach((selectedDivision: any) => {
              if (selectedDivision.organizationId === division.id) {
                division.deliveryMethod = selectedDivision.deliveryMethod;
                const recipientsId = Array.from(
                  selectedDivision.recipients,
                  (obj: any) => obj.id
                );
                division.deliveredTo = recipientsId;
                division.note = selectedDivision.notes || "";
              }
            });
          });
          setResultDivisions(divisions);
        }
      }

      if (data.submitInvoice?.client) {
        setInvoiceDivisionCheckbox(false);
      }

      if (
        data.submitInvoice.subDivisions &&
        data.submitInvoice.subDivisions.length > 0
      ) {
        setInvoiceDivisionCheckbox(true);
        if (invoiceDivisions?.length > 0) {
          const divisions = [...invoiceDivisions];
          const selectedDivisions = data.submitInvoice.subDivisions;
          divisions.forEach((division: any) => {
            selectedDivisions.forEach((selectedDivision: any) => {
              if (selectedDivision.organizationId === division.id) {
                division.deliveryMethod = selectedDivision.deliveryMethod;
                const recipientsId = Array.from(
                  selectedDivision.recipients,
                  (obj: any) => obj.id
                );
                division.deliveredTo = recipientsId;
                division.format = selectedDivision.format;
                division.frequency = selectedDivision.frequency;
                division.note = selectedDivision.notes || "";
              }
            });
          });
          setInvoiceDivisions(divisions);
        }
      }
    }
  }, [data, tenantAllUsers, clientAllUsers, subDivisionList]); // eslint-disable-line

  const formik = useFormik<FormValues>({
    initialValues: {
      orderReceivedDeliveryMethod: data?.orderReceived?.deliveryMethod || "",
      orderReceivedDeliveredTo:
        data?.orderReceived && data?.orderReceived?.recipients
          ? Array.from(data?.orderReceived?.recipients, (obj: any) => obj.id)
          : [],
      resultClientDeliveryMethod:
        data?.submitResults?.client?.deliveryMethod || "",
      resultClientDeliveredTo:
        (data?.submitResults?.client?.recipients &&
          Array.from(
            data?.submitResults?.client?.recipients,
            (obj: any) => obj.id
          )) ||
        [],
      resultClientNote: data?.submitResults?.client?.notes || "",
      invoiceClientFrequency: data?.submitInvoice?.client?.frequency || "",
      invoiceClientDeliveryMethod:
        data?.submitInvoice?.client?.deliveryMethod || "",
      invoiceClientFormat: data?.submitInvoice?.client?.format || "",
      invoiceClientDeliveredTo:
        (data?.submitInvoice?.client?.recipients &&
          Array.from(
            data?.submitInvoice?.client?.recipients,
            (obj: any) => obj.id
          )) ||
        [],
      invoiceClientNote: data?.submitInvoice?.client?.notes || "",
      projectInvoiceDeliveryMethod: data?.projectInvoice?.deliveryMethod || "",
      projectInvoiceFormat: data?.projectInvoice?.format || "",
      projectInvoiceDeliveredTo:
        (data?.projectInvoice?.recipients &&
          Array.from(
            data?.projectInvoice?.recipients,
            (obj: any) => obj.id
          )) ||
        [],
      projectInvoiceNote: data?.projectInvoice?.notes || "",
    },
    validationSchema: ValiditionSchema,

    onSubmit: (values) => {
      if (resultDivisionCheckbox && !isResultDivisionsValid()) {
        return;
      }

      if (invoiceDivisionCheckbox && !isInvoiceDivisionsValid()) {
        return;
      }

      const orderReceived = {
        deliveryMethod: values.orderReceivedDeliveryMethod,
        recipients: values.orderReceivedDeliveredTo,
      };

      const submitResults: any = {};
      if (resultDivisionCheckbox) {
        const divisions: any[] = [];
        resultDivisions.forEach((resultDivision) => {
          const division = {
            organizationId: resultDivision.id,
            deliveryMethod: resultDivision.deliveryMethod,
            recipients: resultDivision.deliveredTo,
            notes: resultDivision.note || null,
          };
          divisions.push(division);
        });
        submitResults.client = null;
        submitResults.subDivisions = divisions;
      } else {
        submitResults.client = {
          deliveryMethod: values.resultClientDeliveryMethod,
          recipients: values.resultClientDeliveredTo,
          notes: values.resultClientNote || null,
        };
        submitResults.subDivisions = null;
      }

      const submitInvoice: any = {};
      if (invoiceDivisionCheckbox) {
        const divisions: any[] = [];
        invoiceDivisions.forEach((invoiceDivision) => {
          const division = {
            organizationId: invoiceDivision.id,
            deliveryMethod: invoiceDivision.deliveryMethod,
            recipients: invoiceDivision.deliveredTo,
            frequency: invoiceDivision.frequency,
            format: invoiceDivision.format,
            notes: invoiceDivision.note || null,
          };
          divisions.push(division);
        });
        submitInvoice.client = null;
        submitInvoice.subDivisions = divisions;
      } else {
        submitInvoice.client = {
          frequency: values.invoiceClientFrequency,
          deliveryMethod: values.invoiceClientDeliveryMethod,
          format: values.invoiceClientFormat,
          recipients: values.invoiceClientDeliveredTo,
          notes: values.invoiceClientNote || null,
        };
        submitInvoice.subDivisions = null;
      }

      const projectInvoice = {
        deliveryMethod: values.projectInvoiceDeliveryMethod,
        format: values.projectInvoiceFormat,
        recipients: values.projectInvoiceDeliveredTo,
        notes: values.projectInvoiceNote || null,
      };

      const payload = {
        orderReceived,
        submitResults,
        submitInvoice,
        projectInvoice,
      };
      updateOrderHandling(payload);
    },
  });

  const isResultDivisionsValid = () => {
    let isValid = true;
    resultDivisions.forEach((division) => {
      if (
        !division.deliveryMethod ||
        (division.deliveredTo && division.deliveredTo.length === 0)
      ) {
        isValid = false;
      }
    });
    return isValid;
  };

  const isInvoiceDivisionsValid = () => {
    let isValid = true;
    invoiceDivisions.forEach((division) => {
      if (
        !division.deliveryMethod ||
        !division.frequency ||
        !division.format ||
        (division.deliveredTo && division.deliveredTo.length === 0)
      ) {
        isValid = false;
      }
    });
    return isValid;
  };

  const handleResultsDivisionDeliveryMethodChange = (
    e: SelectChangeEvent,
    index: number
  ) => {
    const divisions = JSON.parse(JSON.stringify(resultDivisions));
    divisions[index].deliveryMethod = e.target.value;
    setResultDivisions(divisions);
  };

  const handleResultsDivisionDeliveredToChange = (
    e: SelectChangeEvent,
    index: number
  ) => {
    const divisions = JSON.parse(JSON.stringify(resultDivisions));
    divisions[index].deliveredTo = e.target.value;
    setResultDivisions(divisions);
  };

  const handleResultsDivisionNoteChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    const divisions = JSON.parse(JSON.stringify(resultDivisions));
    divisions[index].note = e.target.value;
    setResultDivisions(divisions);
  };

  const handleInvoiveDivisionFrequencyChange = (
    e: SelectChangeEvent,
    index: number
  ) => {
    const divisions = JSON.parse(JSON.stringify(invoiceDivisions));
    divisions[index].frequency = e.target.value;
    setInvoiceDivisions(divisions);
  };

  const handleInvoiveDivisionDeliveryMethodChange = (
    e: SelectChangeEvent,
    index: number
  ) => {
    const divisions = JSON.parse(JSON.stringify(invoiceDivisions));
    divisions[index].deliveryMethod = e.target.value;
    setInvoiceDivisions(divisions);
  };

  const handleInvoiveDivisionFormatChange = (
    e: SelectChangeEvent,
    index: number
  ) => {
    const divisions = JSON.parse(JSON.stringify(invoiceDivisions));
    divisions[index].format = e.target.value;
    setInvoiceDivisions(divisions);
  };

  const handleInvoiveDivisionDeliveredToChange = (
    e: SelectChangeEvent,
    index: number
  ) => {
    const divisions = JSON.parse(JSON.stringify(invoiceDivisions));
    divisions[index].deliveredTo = e.target.value;
    setInvoiceDivisions(divisions);
  };

  const handleInvoiveDivisionNoteChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    const divisions = JSON.parse(JSON.stringify(invoiceDivisions));
    divisions[index].note = e.target.value;
    setInvoiceDivisions(divisions);
  };

  const handleOrederReceivedUserDelete = (id: string) => {
    const filteredUsers = formik.values.orderReceivedDeliveredTo.filter(
      (value) => value !== id
    );
    formik.setFieldValue("orderReceivedDeliveredTo", filteredUsers);
  };

  const handleResultClientUserDelete = (id: string) => {
    const filteredUsers = formik.values.resultClientDeliveredTo.filter(
      (value) => value !== id
    );
    formik.setFieldValue("resultClientDeliveredTo", filteredUsers);
  };

  const handleResultDivisionsUserDelete = (index: number, id: string) => {
    const divisions = [...resultDivisions];
    const filterDeliveredTo = divisions[index].deliveredTo.filter(
      (value: string) => value !== id
    );
    divisions[index].deliveredTo = filterDeliveredTo;
    setResultDivisions(divisions);
  };

  const handleInvoiceClientUserDelete = (id: string) => {
    const filteredUsers = formik.values.invoiceClientDeliveredTo.filter(
      (value) => value !== id
    );
    formik.setFieldValue("invoiceClientDeliveredTo", filteredUsers);
  };

  const handleInvoiceDivisionsUserDelete = (index: number, id: string) => {
    const divisions = [...invoiceDivisions];
    const filterDeliveredTo = divisions[index].deliveredTo.filter(
      (value: string) => value !== id
    );
    divisions[index].deliveredTo = filterDeliveredTo;
    setInvoiceDivisions(divisions);
  };

  const handleProjectInvoiceUserDelete = (id: string) => {
    const filteredUsers = formik.values.projectInvoiceDeliveredTo.filter(
      (value) => value !== id
    );
    formik.setFieldValue("projectInvoiceDeliveredTo", filteredUsers);
  };

  const updateOrderHandling = async (payload: any) => {
    setShowLoading(true);
    const response = await updateOrderHandlingRequest(clientId, payload);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getProcessesProcedureRequest({ id: clientId }));
      closeDialog();
    }
  };

  const getClientUserFullName = (id: string) => {
    const filterUser = clientAllUsers?.filter((user: any) => user.id === id);
    return filterUser && filterUser.length > 0 && filterUser[0].fullName;
  };

  const getTenantUserFullName = (id: string) => {
    const filterUser = tenantAllUsers?.filter((user: any) => user.id === id);
    return filterUser && filterUser.length > 0 && filterUser[0].fullName;
  };

  return (
    <DialogContainer
      title="Client - Processes & Procedures - Order Handling"
      showDialog={showDialog}
      closeDialog={closeDialog}
      onSubmit={formik.handleSubmit}
    >
      <form onSubmit={formik.handleSubmit}>
        <Box py={2.5} display={"flex"}>
          <Typography className="model-label-txt">Order Received</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControl
              fullWidth
              error={
                formik.touched.orderReceivedDeliveryMethod &&
                Boolean(formik.errors.orderReceivedDeliveryMethod)
              }
            >
              <InputLabel id="order-delivery-method-label">
                Delivery Method
              </InputLabel>
              <Select
                labelId="order-delivery-method-label"
                id="orderReceivedDeliveryMethod"
                name="orderReceivedDeliveryMethod"
                label="Delivery Method"
                value={formik.values.orderReceivedDeliveryMethod}
                onChange={formik.handleChange}
              >
                {deliveryMethodOptions
                  .filter((method) => method.value !== "NoAutomatedDelivery")
                  .map((method) => (
                    <MenuItem key={method.value} value={method.value}>
                      {renderDeliveryMethodLabel(method)}
                    </MenuItem>
                  ))}
              </Select>
              {Boolean(formik.errors.orderReceivedDeliveryMethod) &&
                formik.touched.orderReceivedDeliveryMethod && (
                  <Typography
                    sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                    color="error"
                  >
                    This field can't be empty
                  </Typography>
                )}
            </FormControl>
            <FormControl
              fullWidth
              sx={{ mt: 2.5 }}
              error={
                formik.touched.orderReceivedDeliveredTo &&
                Boolean(formik.errors.orderReceivedDeliveredTo)
              }
            >
              <InputLabel id="demo-multiple-chip-label">
                Delivered To
              </InputLabel>
              <Select
                labelId="demo-multiple-chip-label"
                id="orderReceivedDeliveredTo"
                name="orderReceivedDeliveredTo"
                multiple
                value={formik.values.orderReceivedDeliveredTo}
                onChange={formik.handleChange}
                input={
                  <OutlinedInput
                    id="select-multiple-chip"
                    label="Delivered To"
                  />
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        color="primary"
                        variant="outlined"
                        onDelete={(e) => handleOrederReceivedUserDelete(value)}
                        key={value}
                        label={getTenantUserFullName(value)}
                        deleteIcon={
                          <IconButton
                            onMouseDown={(e) => e.stopPropagation()}
                            size="small"
                            sx={{ padding: 0.5 }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        }
                      />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {tenantAllUsers?.map((user: any) => (
                  <MenuItem
                    key={user.id}
                    value={user.id}
                    style={getStyles(
                      user.id,
                      formik.values.orderReceivedDeliveredTo,
                      theme
                    )}
                  >
                    {user.fullName}
                  </MenuItem>
                ))}
              </Select>
              {Boolean(formik.errors.orderReceivedDeliveredTo) &&
                formik.touched.orderReceivedDeliveredTo && (
                  <Typography
                    sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                    color="error"
                  >
                    This field can't be empty
                  </Typography>
                )}
            </FormControl>
          </Box>
        </Box>

        <Divider />

        <Box py={2} display={"flex"}>
          <Typography className="model-label-txt">Submit Results</Typography>
          <Box mt={-1.5} sx={{ width: "calc(100% - 180px)" }}>
            {subDivisionList && subDivisionList.length > 0 && (
              <FormGroup sx={{ ml: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={resultDivisionCheckbox}
                      onChange={(e) =>
                        setResultDivisionCheckbox(e.target.checked)
                      }
                    />
                  }
                  label="Submission Varies by Division"
                />
              </FormGroup>
            )}

            {resultDivisionCheckbox ? (
              <>
                {subDivisionList?.map((division: any, index: number) => (
                  <Box>
                    <Typography fontWeight={600} mt={index === 0 ? 1 : 2.5}>
                      {division.displayName}
                    </Typography>
                    <Box>
                      <FormControl
                        fullWidth
                        sx={{ mt: 2 }}
                        error={
                          !resultDivisions[index]?.deliveryMethod &&
                          formik.submitCount > 0
                        }
                      >
                        <InputLabel id="demo-simple-select-label">
                          Delivery Method
                        </InputLabel>
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={resultDivisions[index]?.deliveryMethod}
                          label="Delivery Method"
                          onChange={(e) =>
                            handleResultsDivisionDeliveryMethodChange(e, index)
                          }
                        >
                          {deliveryMethodOptions.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {renderDeliveryMethodLabel(
                                item,
                                item.value === "Email"
                                  ? "Attach File to Email"
                                  : undefined
                              )}
                            </MenuItem>
                          ))}
                        </Select>
                        {!resultDivisions[index]?.deliveryMethod &&
                          formik.submitCount > 0 && (
                            <Typography
                              sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                              color="error"
                            >
                              This field can't be empty
                            </Typography>
                          )}
                      </FormControl>

                      <FormControl
                        fullWidth
                        sx={{ mt: 2 }}
                        error={
                          resultDivisions[index]?.deliveredTo &&
                          resultDivisions[index]?.deliveredTo.length === 0 &&
                          formik.submitCount > 0
                        }
                      >
                        <InputLabel id={`demo-multiple-chip-label${index}`}>
                          Delivery To
                        </InputLabel>
                        <Select
                          labelId={`demo-multiple-chip-label${index}`}
                          id={`demo-multiple-chip${index}`}
                          multiple
                          value={resultDivisions[index]?.deliveredTo || []}
                          onChange={(e) =>
                            handleResultsDivisionDeliveredToChange(e, index)
                          }
                          input={
                            <OutlinedInput
                              id="select-multiple-chip"
                              label="Delivery To"
                            />
                          }
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((value: string) => (
                                <Chip
                                  color="primary"
                                  variant="outlined"
                                  onDelete={(e) =>
                                    handleResultDivisionsUserDelete(
                                      index,
                                      value
                                    )
                                  }
                                  key={value}
                                  label={getClientUserFullName(value)}
                                  deleteIcon={
                                    <IconButton
                                      onMouseDown={(e) => e.stopPropagation()}
                                      size="small"
                                      sx={{ padding: 0.5 }}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  }
                                />
                              ))}
                            </Box>
                          )}
                          MenuProps={MenuProps}
                        >
                          {clientAllUsers?.map((user: any) => (
                            <MenuItem
                              key={user.id}
                              value={user.id}
                              style={getStyles(
                                user.id,
                                resultDivisions[index]?.deliveredTo,
                                theme
                              )}
                            >
                              {user.fullName}
                            </MenuItem>
                          ))}
                        </Select>
                        {resultDivisions[index]?.deliveredTo &&
                          resultDivisions[index]?.deliveredTo.length === 0 &&
                          formik.submitCount > 0 && (
                            <Typography
                              sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                              color="error"
                            >
                              This field can't be empty
                            </Typography>
                          )}
                      </FormControl>

                      <TextField
                        fullWidth
                        label="Delivery Note"
                        sx={{ mt: 2 }}
                        multiline
                        rows={2}
                        value={resultDivisions[index]?.note || ""}
                        onChange={(e) =>
                          handleResultsDivisionNoteChange(e, index)
                        }
                      />
                    </Box>
                  </Box>
                ))}
              </>
            ) : (
              // if no division
              <Box>
                <FormControl
                  fullWidth
                  sx={{ mt: 1 }}
                  error={
                    formik.touched.resultClientDeliveryMethod &&
                    Boolean(formik.errors.resultClientDeliveryMethod)
                  }
                >
                  <InputLabel id="demo-simple-select-label">
                    Delivery Method
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="resultClientDeliveryMethod"
                    name="resultClientDeliveryMethod"
                    label="Delivery Method"
                    value={formik.values.resultClientDeliveryMethod}
                    onChange={formik.handleChange}
                  >
                    {deliveryMethodOptions.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {renderDeliveryMethodLabel(
                          item,
                          item.value === "Email"
                            ? "Attach File to Email"
                            : undefined
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                  {Boolean(formik.errors.resultClientDeliveryMethod) &&
                    formik.touched.resultClientDeliveryMethod && (
                      <Typography
                        sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                        color="error"
                      >
                        This field can't be empty
                      </Typography>
                    )}
                </FormControl>

                <FormControl
                  fullWidth
                  sx={{ mt: 2 }}
                  error={
                    formik.touched.resultClientDeliveredTo &&
                    Boolean(formik.errors.resultClientDeliveredTo)
                  }
                >
                  <InputLabel id="demo-multiple-chip-label">
                    Delivery To
                  </InputLabel>
                  <Select
                    labelId="demo-multiple-chip-label"
                    id="resultClientDeliveredTo"
                    name="resultClientDeliveredTo"
                    multiple
                    value={formik.values.resultClientDeliveredTo}
                    onChange={formik.handleChange}
                    input={
                      <OutlinedInput
                        id="select-multiple-chip"
                        label="Delivery To"
                      />
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            color="primary"
                            variant="outlined"
                            onDelete={(e) =>
                              handleResultClientUserDelete(value)
                            }
                            key={value}
                            label={getClientUserFullName(value)}
                            deleteIcon={
                              <IconButton
                                onMouseDown={(e) => e.stopPropagation()}
                                size="small"
                                sx={{ padding: 0.5 }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            }
                          />
                        ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {clientAllUsers?.map((user: any) => (
                      <MenuItem
                        key={user.id}
                        value={user.id}
                        style={getStyles(
                          user.id,
                          formik.values.resultClientDeliveredTo,
                          theme
                        )}
                      >
                        {user.fullName}
                      </MenuItem>
                    ))}
                  </Select>
                  {Boolean(formik.errors.resultClientDeliveredTo) &&
                    formik.touched.resultClientDeliveredTo && (
                      <Typography
                        sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                        color="error"
                      >
                        This field can't be empty
                      </Typography>
                    )}
                </FormControl>

                <TextField
                  fullWidth
                  label="Delivery Note"
                  sx={{ mt: 2 }}
                  id="resultClientNote"
                  name="resultClientNote"
                  multiline
                  rows={2}
                  value={formik.values.resultClientNote}
                  onChange={formik.handleChange}
                />
              </Box>
            )}
          </Box>
        </Box>

        <Divider />

        <Box py={2} display={"flex"}>
          <Typography className="model-label-txt">Submit Invoice</Typography>
          <Box mt={-1.5} sx={{ width: "calc(100% - 180px)" }}>
            {subDivisionList && subDivisionList.length > 0 && (
              <FormGroup sx={{ ml: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={invoiceDivisionCheckbox}
                      onChange={(e) =>
                        setInvoiceDivisionCheckbox(e.target.checked)
                      }
                    />
                  }
                  label="Submission Varies by Division"
                />
              </FormGroup>
            )}
            {invoiceDivisionCheckbox ? (
              <>
                {subDivisionList?.map((division: any, index: number) => (
                  <>
                    <Typography fontWeight={600} mt={index === 0 ? 1 : 2.5}>
                      {division.displayName}
                    </Typography>
                    <Box>
                      <FormControl
                        fullWidth
                        sx={{ mt: 2 }}
                        error={
                          !invoiceDivisions[index]?.frequency &&
                          formik.submitCount > 0
                        }
                      >
                        <InputLabel id="demo-simple-select-label">
                          Frequency
                        </InputLabel>
                        <Select
                          labelId="demo-simple-select-label"
                          id="invoiceClientFrequency"
                          label="Frequency"
                          value={invoiceDivisions[index]?.frequency}
                          onChange={(e) =>
                            handleInvoiveDivisionFrequencyChange(e, index)
                          }
                        >
                          {invoiceFrequencyOptions.map((item) => (
                            <MenuItem value={item.value}>
                              {renderDeliveryMethodLabel(
                                item,
                                item.value === "Email"
                                  ? "Attach File to Email"
                                  : undefined
                              )}
                            </MenuItem>
                          ))}
                        </Select>
                        {!invoiceDivisions[index]?.frequency &&
                          formik.submitCount > 0 && (
                            <Typography
                              sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                              color="error"
                            >
                              This field can't be empty
                            </Typography>
                          )}
                      </FormControl>
                    </Box>
                    <Box width={"100%"}>
                      <FormControl
                        sx={{ width: "calc(50% - 8px)", mt: 2 }}
                        error={
                          !invoiceDivisions[index]?.deliveryMethod &&
                          formik.submitCount > 0
                        }
                      >
                        <InputLabel id="demo-simple-select-label">
                          Delivery Method
                        </InputLabel>
                        <Select
                          labelId="demo-simple-select-label"
                          id="invoiceClientDeliveryMethod"
                          label="Delivery Method"
                          value={invoiceDivisions[index]?.deliveryMethod}
                          onChange={(e) =>
                            handleInvoiveDivisionDeliveryMethodChange(e, index)
                          }
                        >
                          {deliveryMethodOptions.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {renderDeliveryMethodLabel(
                                item,
                                item.value === "Email"
                                  ? "Attach File to Email"
                                  : undefined
                              )}
                            </MenuItem>
                          ))}
                        </Select>
                        {!invoiceDivisions[index]?.deliveryMethod &&
                          formik.submitCount > 0 && (
                            <Typography
                              sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                              color="error"
                            >
                              This field can't be empty
                            </Typography>
                          )}
                      </FormControl>
                      <FormControl
                        sx={{ width: "calc(50% - 8px)", mt: 2, ml: 2 }}
                        error={
                          !invoiceDivisions[index]?.format &&
                          formik.submitCount > 0
                        }
                      >
                        <InputLabel id="demo-simple-select-label">
                          Format
                        </InputLabel>
                        <Select
                          labelId="demo-simple-select-label"
                          id="invoiceClientFormat"
                          label="Format"
                          value={invoiceDivisions[index]?.format}
                          onChange={(e) =>
                            handleInvoiveDivisionFormatChange(e, index)
                          }
                        >
                          {invoiceFormatOptions.map((item) => (
                            <MenuItem value={item}>{item}</MenuItem>
                          ))}
                        </Select>
                        {!invoiceDivisions[index]?.format &&
                          formik.submitCount > 0 && (
                            <Typography
                              sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                              color="error"
                            >
                              This field can't be empty
                            </Typography>
                          )}
                      </FormControl>
                    </Box>
                    <Box>
                      <FormControl
                        fullWidth
                        sx={{ mt: 2 }}
                        error={
                          invoiceDivisions[index]?.deliveredTo &&
                          invoiceDivisions[index]?.deliveredTo.length === 0 &&
                          formik.submitCount > 0
                        }
                      >
                        <InputLabel id="demo-multiple-chip-label">
                          Billing Contact
                        </InputLabel>
                        <Select
                          labelId="demo-multiple-chip-label"
                          id="invoiceClientDeliveredTo"
                          multiple
                          value={invoiceDivisions[index]?.deliveredTo || []}
                          onChange={(e) =>
                            handleInvoiveDivisionDeliveredToChange(e, index)
                          }
                          input={
                            <OutlinedInput
                              id="select-multiple-chip"
                              label="Billing Contact"
                            />
                          }
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((value: string) => (
                                <Chip
                                  color="primary"
                                  variant="outlined"
                                  onDelete={(e) =>
                                    handleInvoiceDivisionsUserDelete(
                                      index,
                                      value
                                    )
                                  }
                                  key={value}
                                  label={getClientUserFullName(value)}
                                  deleteIcon={
                                    <IconButton
                                      onMouseDown={(e) => e.stopPropagation()}
                                      size="small"
                                      sx={{ padding: 0.5 }}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  }
                                />
                              ))}
                            </Box>
                          )}
                          MenuProps={MenuProps}
                        >
                          {clientAllUsers?.map((user: any) => (
                            <MenuItem
                              key={user.id}
                              value={user.id}
                              style={getStyles(
                                user.id,
                                invoiceDivisions[index]?.deliveredTo,
                                theme
                              )}
                            >
                              {user.fullName}
                            </MenuItem>
                          ))}
                        </Select>
                        {invoiceDivisions[index]?.deliveredTo &&
                          invoiceDivisions[index]?.deliveredTo.length === 0 &&
                          formik.submitCount > 0 && (
                            <Typography
                              sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                              color="error"
                            >
                              This field can't be empty
                            </Typography>
                          )}
                      </FormControl>

                      <TextField
                        fullWidth
                        label="Delivery Note"
                        sx={{ mt: 2 }}
                        id="invoiceClientNote"
                        multiline
                        rows={2}
                        value={invoiceDivisions[index]?.note || ""}
                        onChange={(e) =>
                          handleInvoiveDivisionNoteChange(e, index)
                        }
                      />
                    </Box>
                  </>
                ))}
              </>
            ) : (
              <>
                <Box>
                  <FormControl
                    fullWidth
                    sx={{ mt: 1 }}
                    error={
                      formik.touched.invoiceClientFrequency &&
                      Boolean(formik.errors.invoiceClientFrequency)
                    }
                  >
                    <InputLabel id="demo-simple-select-label">
                      Frequency
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="invoiceClientFrequency"
                      name="invoiceClientFrequency"
                      label="Frequency"
                      value={formik.values.invoiceClientFrequency}
                      onChange={formik.handleChange}
                    >
                      {invoiceFrequencyOptions.map((item) => (
                        <MenuItem value={item.value}>
                          {renderDeliveryMethodLabel(item)}
                        </MenuItem>
                      ))}
                    </Select>
                    {Boolean(formik.errors.invoiceClientFrequency) &&
                      formik.touched.invoiceClientFrequency && (
                        <Typography
                          sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                          color="error"
                        >
                          This field can't be empty
                        </Typography>
                      )}
                  </FormControl>
                </Box>
                <Box width={"100%"}>
                  <FormControl
                    sx={{ width: "calc(50% - 8px)", mt: 2 }}
                    error={
                      formik.touched.invoiceClientDeliveryMethod &&
                      Boolean(formik.errors.invoiceClientDeliveryMethod)
                    }
                  >
                    <InputLabel id="demo-simple-select-label">
                      Delivery Method
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="invoiceClientDeliveryMethod"
                      name="invoiceClientDeliveryMethod"
                      label="Delivery Method"
                      value={formik.values.invoiceClientDeliveryMethod}
                      onChange={formik.handleChange}
                    >
                      {deliveryMethodOptions.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {renderDeliveryMethodLabel(
                            item,
                            item.value === "Email"
                              ? "Attach File to Email"
                              : undefined
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                    {Boolean(formik.errors.invoiceClientDeliveryMethod) &&
                      formik.touched.invoiceClientDeliveryMethod && (
                        <Typography
                          sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                          color="error"
                        >
                          This field can't be empty
                        </Typography>
                      )}
                  </FormControl>
                  <FormControl
                    sx={{ width: "calc(50% - 8px)", mt: 2, ml: 2 }}
                    error={
                      formik.touched.invoiceClientFormat &&
                      Boolean(formik.errors.invoiceClientFormat)
                    }
                  >
                    <InputLabel id="demo-simple-select-label">
                      Format
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="invoiceClientFormat"
                      name="invoiceClientFormat"
                      label="Format"
                      value={formik.values.invoiceClientFormat}
                      onChange={formik.handleChange}
                    >
                      {invoiceFormatOptions.map((item) => (
                        <MenuItem value={item}>{item}</MenuItem>
                      ))}
                    </Select>
                    {Boolean(formik.errors.invoiceClientFormat) &&
                      formik.touched.invoiceClientFormat && (
                        <Typography
                          sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                          color="error"
                        >
                          This field can't be empty
                        </Typography>
                      )}
                  </FormControl>
                </Box>
                <Box>
                  <FormControl
                    fullWidth
                    sx={{ mt: 2 }}
                    error={
                      formik.touched.invoiceClientDeliveredTo &&
                      Boolean(formik.errors.invoiceClientDeliveredTo)
                    }
                  >
                    <InputLabel id="demo-multiple-chip-label">
                      Billing Contact
                    </InputLabel>
                    <Select
                      labelId="demo-multiple-chip-label"
                      id="invoiceClientDeliveredTo"
                      name="invoiceClientDeliveredTo"
                      multiple
                      value={formik.values.invoiceClientDeliveredTo}
                      onChange={formik.handleChange}
                      input={
                        <OutlinedInput
                          id="select-multiple-chip"
                          label="Billing Contact"
                        />
                      }
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              color="primary"
                              variant="outlined"
                              onDelete={(e) =>
                                handleInvoiceClientUserDelete(value)
                              }
                              key={value}
                              label={getClientUserFullName(value)}
                              deleteIcon={
                                <IconButton
                                  onMouseDown={(e) => e.stopPropagation()}
                                  size="small"
                                  sx={{ padding: 0.5 }}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              }
                            />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {clientAllUsers?.map((user: any) => (
                        <MenuItem
                          key={user.id}
                          value={user.id}
                          style={getStyles(
                            user.id,
                            formik.values.invoiceClientDeliveredTo,
                            theme
                          )}
                        >
                          {user.fullName}
                        </MenuItem>
                      ))}
                    </Select>
                    {Boolean(formik.errors.invoiceClientDeliveredTo) &&
                      formik.touched.invoiceClientDeliveredTo && (
                        <Typography
                          sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                          color="error"
                        >
                          This field can't be empty
                        </Typography>
                      )}
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Delivery Note"
                    sx={{ mt: 2 }}
                    id="invoiceClientNote"
                    name="invoiceClientNote"
                    multiline
                    rows={2}
                    value={formik.values.invoiceClientNote}
                    onChange={formik.handleChange}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>

        <Divider />

        <Box py={2} display={"flex"}>
          <Typography className="model-label-txt">Project Invoice</Typography>
          <Box mt={-1.5} sx={{ width: "calc(100% - 180px)" }}>
            <Box>
              <FormControl
                fullWidth
                sx={{ mt: 1 }}
                error={
                  formik.touched.projectInvoiceDeliveryMethod &&
                  Boolean(formik.errors.projectInvoiceDeliveryMethod)
                }
              >
                <InputLabel id="demo-simple-select-label">
                  Delivery Method
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="projectInvoiceDeliveryMethod"
                  name="projectInvoiceDeliveryMethod"
                  label="Delivery Method"
                  value={formik.values.projectInvoiceDeliveryMethod}
                  onChange={formik.handleChange}
                >
                  {deliveryMethodOptions.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {renderDeliveryMethodLabel(
                        item,
                        item.value === "Email"
                          ? "Attach File to Email"
                          : undefined
                      )}
                    </MenuItem>
                  ))}
                </Select>
                {Boolean(formik.errors.projectInvoiceDeliveryMethod) &&
                  formik.touched.projectInvoiceDeliveryMethod && (
                    <Typography
                      sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                      color="error"
                    >
                      This field can't be empty
                    </Typography>
                  )}
              </FormControl>
              <FormControl
                fullWidth
                sx={{ mt: 2 }}
                error={
                  formik.touched.projectInvoiceFormat &&
                  Boolean(formik.errors.projectInvoiceFormat)
                }
              >
                <InputLabel id="demo-simple-select-label">Format</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="projectInvoiceFormat"
                  name="projectInvoiceFormat"
                  label="Format"
                  value={formik.values.projectInvoiceFormat}
                  onChange={formik.handleChange}
                >
                  {invoiceFormatOptions.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
                {Boolean(formik.errors.projectInvoiceFormat) &&
                  formik.touched.projectInvoiceFormat && (
                    <Typography
                      sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                      color="error"
                    >
                      This field can't be empty
                    </Typography>
                  )}
              </FormControl>
            </Box>
            <Box>
              <FormControl
                fullWidth
                sx={{ mt: 2 }}
                error={
                  formik.touched.projectInvoiceDeliveredTo &&
                  Boolean(formik.errors.projectInvoiceDeliveredTo)
                }
              >
                <InputLabel id="demo-multiple-chip-label">
                  Billing Contact
                </InputLabel>
                <Select
                  labelId="demo-multiple-chip-label"
                  id="projectInvoiceDeliveredTo"
                  name="projectInvoiceDeliveredTo"
                  multiple
                  value={formik.values.projectInvoiceDeliveredTo}
                  onChange={formik.handleChange}
                  input={
                    <OutlinedInput
                      id="select-multiple-chip"
                      label="Billing Contact"
                    />
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          color="primary"
                          variant="outlined"
                          onDelete={(e) => handleProjectInvoiceUserDelete(value)}
                          key={value}
                          label={getClientUserFullName(value)}
                          deleteIcon={
                            <IconButton
                              onMouseDown={(e) => e.stopPropagation()}
                              size="small"
                              sx={{ padding: 0.5 }}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          }
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {clientAllUsers?.map((user: any) => (
                    <MenuItem
                      key={user.id}
                      value={user.id}
                      style={getStyles(
                        user.id,
                        formik.values.projectInvoiceDeliveredTo,
                        theme
                      )}
                    >
                      {user.fullName}
                    </MenuItem>
                  ))}
                </Select>
                {Boolean(formik.errors.projectInvoiceDeliveredTo) &&
                  formik.touched.projectInvoiceDeliveredTo && (
                    <Typography
                      sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                      color="error"
                    >
                      This field can't be empty
                    </Typography>
                  )}
              </FormControl>

              <TextField
                fullWidth
                label="Invoice Note"
                sx={{ mt: 2 }}
                id="projectInvoiceNote"
                name="projectInvoiceNote"
                multiline
                rows={2}
                value={formik.values.projectInvoiceNote}
                onChange={formik.handleChange}
              />
            </Box>
          </Box>
        </Box>
      </form>
      {showLoading && <Loading />}
    </DialogContainer>
  );
}
